import { Fragment, type JSX } from "react";
import { Link } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { AlertTriangle, Info, Megaphone, type LucideIcon } from "lucide-react";
import { addDays, endOfMonth, format, parseISO } from "date-fns";
import type {
  AnalyticsAggregateRow,
  AnalyticsBucket,
  AnalyticsFieldKey,
} from "@adlywise/shared";
import DataTable, { DATA_TABLE_CLASSES } from "@/components/common/DataTable";
import { useAnalyticsNameMaps } from "@/features/analytics/hooks/useAnalyticsNameMaps";
import {
  FIELD_LABELS,
  metricGroupsFor,
  type AnalyticsConfig,
} from "./ConfigurePanel";
import { AnalyticsEmptyState } from "./AnalyticsEmptyState";
import { AnalyticsLoadingState } from "./AnalyticsLoadingState";

// Aggregated-results table. Column order matches the user's sketch:
//   Bucket | <metric groups (total/devices)> | <ordered dimension cols>
// One metric GROUP per configured metric (source=requests → "Requests";
// source=responses → one per selected status), each with two sub-columns
// {total, devices}, so the header is TWO rows. Body cells read
// row["<key>_total"] / row["<key>_devices"] in metric-group order. Entity
// dimensions (campaign/segment/creative/provider) render their resolved name as
// a link to the entity (campaign also shows its icon); other dims are plain
// text. Handles loading / empty / error / not-run, a truncated banner, and a
// distinctDevices≪total note.

type AggregatedResultsTableProps = {
  rows: AnalyticsAggregateRow[];
  config: AnalyticsConfig;
  truncated: boolean;
  hasRun: boolean;
  isLoading: boolean;
  errorMessage: string;
};

const formatMetric = (value: unknown): string =>
  Number(value ?? 0).toLocaleString("en-US");

const DATE_FMT = "yyyy-MM-dd";

// The bucket string is Kyiv wall-clock "YYYY-MM-DDThh:mm:ss". We format from the
// DATE part only (parsed as a local date) so there is no TZ drift, then present
// per granularity:
//   hour  → "YYYY-MM-DD HH:00"
//   day   → "YYYY-MM-DD"
//   week  → "<Mon> – <Sun>" (start .. start+6)
//   month → "<1st> – <last>" (first .. end of month)
const formatBucket = (bucket: string, granularity: AnalyticsBucket): string => {
  const datePart = bucket.slice(0, 10);
  const start = parseISO(datePart);
  if (Number.isNaN(start.getTime())) return bucket;

  switch (granularity) {
    case "hour": {
      const hh = bucket.slice(11, 13) || "00";
      return `${datePart} ${hh}:00`;
    }
    case "day":
      return datePart;
    case "week":
      return `${format(start, DATE_FMT)} – ${format(addDays(start, 6), DATE_FMT)}`;
    case "month":
      return `${format(start, DATE_FMT)} – ${format(endOfMonth(start), DATE_FMT)}`;
    default:
      return datePart;
  }
};

// Resolve a campaign's lucide icon component from its stored IconName (a lucide
// PascalCase export, per the new data model). Falls back to Megaphone (the same
// default the campaigns table uses). Mirrors getRowIcon's primary branch without
// the legacy parseItemIcon shim — analytics campaigns always carry a real icon.
const campaignIconComponent = (iconName: string | null): LucideIcon => {
  if (iconName) {
    const icon = (LucideIcons as unknown as Record<string, LucideIcon>)[
      iconName
    ];
    if (icon) return icon;
  }
  return Megaphone;
};

// Entity dimensions link the resolved name to the entity. campaign/segment have
// per-id edit routes; creative/provider have no per-id route → link to the list.
const entityLinkTo = (
  key: AnalyticsFieldKey,
  id: string,
): string | null => {
  switch (key) {
    case "campaign":
      return `/dashboard/campaigns/${id}/edit`;
    case "segment":
      return `/dashboard/segments/${id}/edit`;
    case "creative":
      return `/dashboard/creatives`;
    case "provider":
      return `/dashboard/providers`;
    default:
      return null;
  }
};

export const AggregatedResultsTable = ({
  rows,
  config,
  truncated,
  hasRun,
  isLoading,
  errorMessage,
}: AggregatedResultsTableProps): JSX.Element => {
  const groupBy: AnalyticsFieldKey[] = config.groupBy;
  const metricGroups = metricGroupsFor(config);
  const { names, campaignIcons } = useAnalyticsNameMaps(rows, groupBy);

  const nameFor = (key: AnalyticsFieldKey, id: string): string =>
    names[key]?.[id] ?? id;

  // Render a single dimension cell's content: entity dims → linked name (campaign
  // also gets its icon); everything else → resolved plain text. Only linkify when
  // the id resolved to a real entity name.
  const renderDimCell = (key: AnalyticsFieldKey, raw: unknown): JSX.Element => {
    if (raw == null) return <span className="text-(--text-muted)">—</span>;

    // categories is a text[] — resolve + comma-join members, plain text.
    if (Array.isArray(raw)) {
      if (raw.length === 0)
        return <span className="text-(--text-muted)">—</span>;
      return (
        <span>{raw.map((m) => nameFor(key, String(m))).join(", ")}</span>
      );
    }

    const id = String(raw);
    const to = entityLinkTo(key, id);
    const resolvedName = names[key]?.[id];
    // Only linkify entity dims that actually resolved to a real name.
    if (to && resolvedName) {
      if (key === "campaign") {
        const Icon = campaignIconComponent(campaignIcons[id] ?? null);
        return (
          <Link
            to={to}
            className="inline-flex items-center gap-2 text-(--text-primary) no-underline hover:underline"
            title={resolvedName}
          >
            <Icon
              size={15}
              strokeWidth={1.8}
              className="shrink-0 text-(--brand-primary)"
            />
            <span>{resolvedName}</span>
          </Link>
        );
      }
      return (
        <Link
          to={to}
          className="text-(--text-primary) no-underline hover:underline"
          title={resolvedName}
        >
          {resolvedName}
        </Link>
      );
    }

    return <span>{nameFor(key, id)}</span>;
  };

  if (isLoading) {
    return <AnalyticsLoadingState />;
  }

  if (errorMessage) {
    return (
      <div className="flex min-h-40 items-center justify-center rounded-(--radius-lg) border border-(--status-danger)/30 bg-(--pastel-rose) px-6 py-8 text-center text-[13px] text-(--status-danger)">
        {errorMessage}
      </div>
    );
  }

  if (!hasRun) {
    return (
      <div className="flex min-h-40 items-center justify-center rounded-(--radius-lg) border border-dashed border-(--border-subtle) px-6 py-10 text-center text-[13px] text-(--text-secondary)">
        Configure the report and run it to see results.
      </div>
    );
  }

  if (rows.length === 0) {
    return <AnalyticsEmptyState />;
  }

  return (
    <div className="flex flex-col gap-3">
      {truncated && (
        <div className="flex items-center gap-2 rounded-(--radius-md) border border-(--status-warn)/30 bg-(--pastel-amber) px-4 py-2.5 text-[12.5px] text-(--text-primary)">
          <AlertTriangle size={15} className="shrink-0 text-(--status-warn)" />
          <span>
            Results truncated (ordered): only the first 5,000 rows are shown.
            Narrow the date range or reduce the grouping to see everything.
          </span>
        </div>
      )}

      <DataTable>
        <thead>
          {/* Row 1: bucket (spans both header rows) + one spanning cell per
              metric group + dimension cols (span both header rows). Metrics come
              right after the bucket, before the dimensions. */}
          <tr className={DATA_TABLE_CLASSES.headRow}>
            <th className={DATA_TABLE_CLASSES.th} rowSpan={2}>
              Bucket
            </th>
            {metricGroups.map((group) => (
              <th
                key={group.key}
                colSpan={2}
                className={`${DATA_TABLE_CLASSES.th} border-l border-(--border-subtle) text-center`}
              >
                {group.label}
              </th>
            ))}
            {groupBy.map((key) => (
              <th
                key={key}
                className={`${DATA_TABLE_CLASSES.th} border-l border-(--border-subtle)`}
                rowSpan={2}
              >
                {FIELD_LABELS[key]}
              </th>
            ))}
          </tr>
          {/* Row 2: total / devices under each metric group. */}
          <tr className={DATA_TABLE_CLASSES.headRow}>
            {metricGroups.map((group) => (
              <Fragment key={group.key}>
                <th
                  className={`${DATA_TABLE_CLASSES.thRight} border-l border-(--border-subtle) font-normal`}
                >
                  total
                </th>
                <th className={`${DATA_TABLE_CLASSES.thRight} font-normal`}>
                  devices
                </th>
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={`${row.bucket}-${index.toString()}`}
              className={DATA_TABLE_CLASSES.bodyRow}
            >
              <td
                className={`${DATA_TABLE_CLASSES.td} text-[13.5px] font-bold text-(--text-primary) whitespace-nowrap`}
              >
                {formatBucket(row.bucket, config.bucket)}
              </td>
              {metricGroups.map((group) => (
                <Fragment key={group.key}>
                  <td
                    className={`${DATA_TABLE_CLASSES.tdRight} border-l border-(--border-subtle) text-[13.5px] font-bold text-(--text-primary) tabular-nums`}
                  >
                    {formatMetric(row[`${group.key}_total`])}
                  </td>
                  <td
                    className={`${DATA_TABLE_CLASSES.tdRight} text-[13.5px] font-bold text-(--text-primary) tabular-nums`}
                  >
                    {formatMetric(row[`${group.key}_devices`])}
                  </td>
                </Fragment>
              ))}
              {groupBy.map((key) => (
                <td
                  key={key}
                  className={`${DATA_TABLE_CLASSES.td} border-l border-(--border-subtle) text-[13.5px] text-(--text-primary)`}
                >
                  {renderDimCell(key, row[key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </DataTable>

      <p className="flex items-start gap-1.5 text-[12px] text-(--text-muted)">
        <Info size={13} className="mt-0.5 shrink-0" />
        <span>
          Distinct devices can be far below total on deviceId-sparse traffic
          (e.g. RTB), where many rows carry no device id (those are excluded from
          the distinct count).
        </span>
      </p>
    </div>
  );
};
